// src/components/sourcex/branchLayout.js
//
// Visual nesting for conditional fields, shared by the template editor
// (SectionEditor) and the patient source document (PatientSource) so both read
// the same shape. Presentation only — nothing here decides whether a field is
// visible, just how far it is indented and where the connector line stops.

import { conditionLeaves } from "../../services/templateSchema";

// A long chain must not march a field off the right edge.
export const MAX_BRANCH_INDENT = 3;

/**
 * How deeply each field is nested by branching: 0 for a field that always
 * shows, 1 for one revealed by an answer, 2 for one revealed by a field that is
 * itself conditional, and so on.
 *
 * A circular rule resolves to 0 rather than recursing forever.
 *
 * @param {Array} questions the event's FULL question list
 * @returns {Map<string, number>} question id -> indent level
 */
export const buildBranchDepth = (questions = []) => {
  const byId = new Map(questions.map((q) => [q.id, q]));
  const memo = new Map();

  const depthOf = (id, seen) => {
    if (memo.has(id)) return memo.get(id);
    if (seen.has(id)) return 0; // circular rule — don't recurse forever
    const q = byId.get(id);
    if (!q?.visibleWhen) {
      memo.set(id, 0);
      return 0;
    }
    const nextSeen = new Set(seen).add(id);
    const parents = conditionLeaves(q.visibleWhen)
      .map((leaf) => leaf.questionId)
      .filter((pid) => pid && pid !== id && byId.has(pid));
    if (!parents.length) {
      // Branches on something outside this visit — still conditional, so it
      // indents one level rather than sitting flush with unconditional fields.
      memo.set(id, 1);
      return 1;
    }
    const d = Math.min(
      MAX_BRANCH_INDENT,
      1 + Math.max(...parents.map((pid) => depthOf(pid, nextSeen)))
    );
    memo.set(id, d);
    return d;
  };

  questions.forEach((q) => depthOf(q.id, new Set()));
  return memo;
};

/**
 * Where each run of nested fields ends, so a connector can draw an elbow that
 * STOPS at the last child (└) instead of running past it (├).
 *
 * Must be given the fields actually being DRAWN — a collapsed heading's fields
 * aren't rendered, and a trunk must not span the gap they'd have occupied.
 *
 * @param {Array} drawn rendered questions, in order
 * @param {Map<string, number>} branchDepth from buildBranchDepth
 * @returns {Map<string, boolean>} question id -> is last of its run
 */
export const buildLastInRun = (drawn = [], branchDepth = new Map()) => {
  const out = new Map();
  drawn.forEach((q, i) => {
    const d = branchDepth.get(q.id) || 0;
    if (d === 0) return;
    const next = drawn[i + 1];
    const nextDepth = next ? branchDepth.get(next.id) || 0 : 0;
    out.set(q.id, !next || next.type === "Header" || nextDepth < d);
  });
  return out;
};

/**
 * Outline numbers for one visit: "1" for a heading, "1.2" for a question under
 * it, "1.2.1" for one that only appears once 1.2 is answered a certain way, and
 * a further level per branch depth after that. The number of dots IS the
 * nesting depth — which is the point: a coordinator and a monitor can say
 * "1.2.1" and mean the same field, without counting indents.
 *
 * A question sitting ABOVE the first heading belongs to no section and is
 * numbered "0.x". That reads as odd on purpose: it is odd, and the alternative
 * (calling it 1.x) would give it the same number as a real question in the
 * first section.
 *
 * Presentation only. These numbers are never written into a question's text or
 * into an export — a filed source document's wording must not change because a
 * heading was reordered above it.
 *
 * @param {Array} questions the event's FULL question list, in order
 * @param {Map<string, number>} branchDepth from buildBranchDepth
 * @returns {Map<string, string>} question id -> display number
 */
export const buildOutlineNumbers = (questions = [], branchDepth = new Map()) => {
  const out = new Map();
  let heading = 0;
  // counters[d] = how many questions have been seen at branch depth d since
  // whatever last reset that level.
  let counters = [];
  for (const q of questions) {
    if (q.type === "Header") {
      heading += 1;
      counters = [];
      out.set(q.id, String(heading));
      continue;
    }
    const d = branchDepth.get(q.id) || 0;
    // Truncating BEFORE incrementing is what makes coming back up a level
    // restart the deeper counter: 1.1.1, 1.1.2, back out to 1.2, then 1.2.1
    // again — rather than 1.2.3 carrying on from the abandoned run.
    counters.length = d + 1;
    counters[d] = (counters[d] || 0) + 1;
    // A gap (a question at depth 2 with nothing at depth 1 before it, which a
    // rule pointing outside this visit can produce) fills as 1 rather than
    // leaving an empty segment like "1..1".
    out.set(
      q.id,
      [heading, ...Array.from(counters, (n) => n || 1)].join(".")
    );
  }
  return out;
};
